import React, { useEffect, useRef, useState } from 'react'
import { Alert, App, Button, Flex, Radio, Space, Spin, Tag, Modal, Tooltip } from 'antd'
import { useParams } from 'react-router'
import PatternIcon from '../../../../../../../../../../components/common/PatternIcon'
import {
    useApproveAllQuestionRubricAnswersByCategoryMutation,
    useFetchAnswerCategoryMismatchPredictionsQuery,
    useFetchQuestionAnswersQuery,
    useReviewQuestionRubricAnswerMutation,
    useUpdateQuestionAnswerEscalatedMutation,
    useUpdateQuestionAnswerReasoningMutation,
} from '../../../../../../../../../../store/apis/submission.api'
import Text from 'antd/es/typography/Text'
import { useDispatch, useSelector } from 'react-redux'
import {
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    FileDoneOutlined,
    LikeOutlined,
    WarningOutlined,
} from '@ant-design/icons'
import textSubmissionDrawerSelectors from '../../../../../../../../../../store/selectors/text-submission-drawer.selectors'
import {
    useTextSubmissionNavigation,
    useCloseAndNavigateToDashboard,
} from '../../../../../../../../../../store/apis/utils/hooks/text-submission.hooks'
import { useThemeColors } from '../../../../../../../../../../hooks/useThemeColors'
import { submissionActions } from '../../../../../../../../../../store/slices/submission.slice'
import { openNotificationWithIcon } from '../../../../../../../../../../utils/notification.helper'
import { isNumber } from 'lodash'
import {
    fetchAnswerCategoryMismatchPredictionsSelectors,
    fetchQuestionAnswersSelectors,
} from '../../../../../../../../../../store/apis/utils/selectors/submission.api.selectors'
import {
    ConfidenceLevels,
    getConfidenceLevelName,
    getConfidenceLevelNoun,
    getConfidenceLevelTagColor,
} from '../../../../../../../../../../constants/confidence-levels'
import assignmentSelectors from '../../../../../../../../../../store/selectors/assignment.selectors'
import { isMba } from '../../../../../../../../../../utils/assignment.helper'
import useQueryParam from '../../../../../../../../../../hooks/useQueryParam'
import {
    ASSIGNEE_IDS_PARAM,
    FLAGGED_PARAM,
    SUBMISSION_SEARCH_PARAM,
} from '../../../../../../../../../../constants/query-params'
import { useVersionId } from '../../../../../../../../../../utils/parseURL.helper'
import { useAnswerContext } from '../../../AnswerContext'
import { useExplanation, useHoverPrefetch } from '../../../../../../../../../../store/apis/utils/hooks/category.hooks'
import { ExplainabilityStatuses } from '../../../../../../../../../../constants/explainability-statuses'
import userSelectors from '../../../../../../../../../../store/selectors/user.selectors'
import MarkdownRenderer from '../../../../../../../../../../components/common/markdown-renderer'
import { truncate } from 'lodash'
const useShortcutSelection = (categories, onChange, onApprove, disabled) => {
    useEffect(() => {
        const handleKeyDown = event => {
            if (disabled) return
            if (event.altKey && event.key >= '1' && event.key <= `${categories?.length}`) {
                const index = parseInt(event.key, 10) - 1
                if (categories?.[index]) {
                    onChange({ target: { value: categories[index].id } })
                }
            }
            if (event.altKey && event.key === 'Enter') onApprove()
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [categories, onChange])
}

const useAutoscrollToSelectedCategory = ({ selectedCategoryId, containerRef }) => {
    const categoryRefs = useRef({})

    useEffect(() => {
        if (selectedCategoryId && categoryRefs.current[selectedCategoryId] && containerRef?.current) {
            const container = containerRef.current
            const categoryElement = categoryRefs.current[selectedCategoryId]

            // Traverse up to the parent element that is a direct child of the container
            const actualChild = categoryElement.closest('.ant-radio-button-wrapper')

            if (!actualChild) return

            // Calculate horizontal scrolling
            const childLeft = actualChild.offsetLeft
            const childRight = childLeft + actualChild.offsetWidth

            // Scroll to bring the element into view within the container
            if (childLeft < container.scrollLeft) {
                container.scrollLeft = childLeft // Scroll left
            } else if (childRight > container.scrollLeft + container.offsetWidth) {
                container.scrollLeft = childRight - container.offsetWidth // Scroll right
            }
        }
    }, [selectedCategoryId, containerRef])

    return categoryRefs
}

const CategorySelect = ({
    questionRubric,
    categories = [],
    submissionId,
    answer,
    showDetails,
    readonly,
    calculateApprovalSpeed,
    onCategoryChange,
    draftPersonalized = null,
    draftCanned = null,
    pendingPersonalizedFeedback = null,
    approveDisabled = false,
}) => {
    const { modal } = App.useApp()
    const colors = useThemeColors()
    const assignmentType = useSelector(assignmentSelectors.type)
    const focusedQuestionRubricId = useSelector(textSubmissionDrawerSelectors.focusedQuestionRubricId)
    const dispatch = useDispatch()
    const versionId = useVersionId()
    const { token, layerId: questionRubricIdParam } = useParams()
    const searchParam = useQueryParam(SUBMISSION_SEARCH_PARAM)
    const questionRubricId = ['all', 'dashboard'].includes(questionRubricIdParam)
        ? null
        : parseInt(questionRubricIdParam)
    const userId = useSelector(userSelectors.id)
    const isOwner = useSelector(assignmentSelectors.isOwner)
    const assigneeIdsParam = useQueryParam(ASSIGNEE_IDS_PARAM)

    const [selectedCategoryId, setSelectedCategoryId] = useState(answer.categoryId)
    const { categoryIdToHighlight, setCategoryIdToHighlight } = useAnswerContext()

    // Update selectedCategoryId when categoryIdToHighlight changes
    useEffect(() => {
        if (categoryIdToHighlight && categories.some(category => category.id === categoryIdToHighlight)) {
            setSelectedCategoryId(categoryIdToHighlight)
        }
    }, [categoryIdToHighlight, categories])

    // Notify parent when selectedCategoryId changes
    useEffect(() => {
        if (onCategoryChange && selectedCategoryId) {
            onCategoryChange(selectedCategoryId)
        }
    }, [selectedCategoryId, onCategoryChange])

    const containerRef = useRef(null) // Ref for the category container
    const categoryRefs = useAutoscrollToSelectedCategory({ selectedCategoryId, containerRef })
    const [reviewQuestionRubricAnswer, reviewQuestionRubricAnswerResult] = useReviewQuestionRubricAnswerMutation()
    const [updateQuestionAnswerReasoning] = useUpdateQuestionAnswerReasoningMutation()
    const [approveAllQuestionRubricAnswersByCategory, approveAllQuestionRubricAnswersByCategoryResult] =
        useApproveAllQuestionRubricAnswersByCategoryMutation()
    const { data: answerCategoryMismatchPrediction } = useFetchAnswerCategoryMismatchPredictionsQuery(
        { questionRubricId },
        {
            skip: !isNumber(questionRubricId) || isMba(assignmentType),
            selectFromResult: result =>
                fetchAnswerCategoryMismatchPredictionsSelectors.byAnswerId({ answerId: answer.id }, result),
        },
    )

    const { data: questionSubmissions } = useFetchQuestionAnswersQuery(
        { id: questionRubricId, search: searchParam, versionId, assigneeIds: isOwner ? undefined : [userId] },
        { skip: !isNumber(questionRubricId) },
    )

    const { explanation, isFetching: isFetchingExplanation } = useExplanation(answer)
    const { handleMouseEnter, handleMouseLeave } = useHoverPrefetch(answer)

    const { navigateToNextUnreviewedAnswer } = useTextSubmissionNavigation()
    const isInEscalatedMode = useQueryParam(FLAGGED_PARAM) === 'true'

    const getExplanationData = () => {
        if (!explanation) {
            return {
                reasoning: draftPersonalized ?? pendingPersonalizedFeedback ?? answer.personalizedFeedback ?? answer.reasoning,
                explainabilityReasoning: answer.explainabilityReasoning,
                highlights: answer.highlights,
                explainabilityStatus: answer.escalated,
                isAdequate: answer.isAdequate,
                ethicalCheckOutcome: answer.ethicalCheckOutcome,
            }
        }

        const isAdequate = explanation.isAdequate
        const reasoningToUse = draftPersonalized ?? pendingPersonalizedFeedback ?? (isAdequate ? explanation.reasoning : null)

        return {
            reasoning: reasoningToUse,
            explainabilityReasoning: explanation.reasoning,
            highlights: explanation.highlights,
            explainabilityStatus:
                answer.escalated === ExplainabilityStatuses.MANUAL_UNRESOLVED
                    ? ExplainabilityStatuses.MANUAL_UNRESOLVED
                    : isAdequate
                      ? ExplainabilityStatuses.UNEXPLAINABLE_RESOLVED
                      : ExplainabilityStatuses.UNEXPLAINABLE_UNRESOLVED,
            isAdequate: explanation.isAdequate,
            ethicalCheckOutcome: explanation.ethicalCheckOutcome,
        }
    }

    const onApprove = async event => {
        if (event) {
            event.stopPropagation() // needed to prevent GradingCard's onClick
            event.preventDefault()
        }

        if (approveDisabled) return

        if (pendingPersonalizedFeedback != null) {
            await updateQuestionAnswerReasoning({
                id: answer.id,
                submissionId,
                reasoning: pendingPersonalizedFeedback,
                questionRubricId: questionRubric?.id ?? answer.questionRubricId,
                feedbackType: 'personalized',
            })
        }

        const explanationData = getExplanationData()

        reviewQuestionRubricAnswer({
            versionId,
            id: answer.id,
            categoryId: selectedCategoryId,
            submissionId,
            questionRubricId: questionRubric?.id ?? answer.questionRubricId,
            search: searchParam,
            token,
            assignmentType,
            mismatchId: answerCategoryMismatchPrediction?.id,
            ...explanationData,
            cannedFeedback: draftCanned,
            assigneeIds: assigneeIdsParam?.split(',')?.map(Number),
            approvalSpeedMs: calculateApprovalSpeed ? calculateApprovalSpeed() : null,
        })

        if (!isInEscalatedMode) {
            navigateToNextUnreviewedAnswer({ lastReviewedQuestionRubricId: questionRubric?.id ?? answer.questionRubricId })
        }
    }

    const approveAll = async (categoryId, mineOnly = true) => {
        const questionRubricId = questionRubric?.id

        const response = await approveAllQuestionRubricAnswersByCategory({
            categoryId,
            questionRubricId,
            mineOnly,
        })
        if ('data' in response) {
            dispatch(submissionActions.closeSubmission())
            openNotificationWithIcon('success', 'All answers with this category have been approved')
        }
    }

    const [updateQuestionAnswerEscalated] = useUpdateQuestionAnswerEscalatedMutation()

    const search = useQueryParam(SUBMISSION_SEARCH_PARAM)

    const onResolveUnexplainable = async () => {
        await updateQuestionAnswerEscalated({
            id: answer.id,
            questionRubricId: answer.questionRubricId,
            submissionId,
            escalated: ExplainabilityStatuses.UNEXPLAINABLE_RESOLVED,
            search,
            versionId,
            token,
        })
    }

    const options = categories?.map(category => {
        const isSelectedCategory = selectedCategoryId === category.id
        const buttonType = focusedQuestionRubricId === answer.questionRubricId ? 'primary' : 'default'

        // Determine if the current category is the AI-recommended category
        const isRecommendedCategory = answerCategoryMismatchPrediction
            ? category.id === answerCategoryMismatchPrediction.predictedCategoryId
            : category.id === answer.categoryId

        // Check if approveAll button should be shown
        const showApproveAll =
            answer.confidence === ConfidenceLevels.VERY_CONFIDENT &&
            questionRubricId === answer.questionRubricId &&
            !answer.approveStatus &&
            !readonly &&
            !approveDisabled &&
            isRecommendedCategory &&
            selectedCategoryId === category.id &&
            category.autoApprove !== false

        const approveButton =
            isSelectedCategory &&
            !readonly &&
            (answer.approveStatus && answer.categoryId === selectedCategoryId ? (
                !answer.isAdequate && answer.escalated !== ExplainabilityStatuses.UNEXPLAINABLE_RESOLVED ? (
                    <Button
                        style={{ backgroundColor: colors.success }}
                        type={buttonType}
                        size='large'
                        block
                        onClick={onResolveUnexplainable}
                        icon={<LikeOutlined />}
                        title='Alt + Enter: Approve rubric as compatible'
                        className='mt-8'
                        data-test='approve-as-compatible-button'
                    >
                        Approve as Compatible
                    </Button>
                ) : (
                    <div className='mt-8 text-center' style={{ color: colors.success, fontSize: '16px' }}>
                        <LikeOutlined title='Approved' style={{ marginRight: '8px' }} />
                        <Text strong style={{ color: colors.success }}>
                            Approved
                        </Text>
                    </div>
                )
            ) : (
                <Tooltip title={approveDisabled ? 'Personalized feedback is required before approving' : null}>
                    <Button
                        type={showApproveAll ? 'default' : buttonType}
                        size='large'
                        block
                        onClick={onApprove}
                        icon={<LikeOutlined />}
                        title='Alt + Enter: Approve this answer'
                        className='mt-8'
                        data-test='approve-button'
                        disabled={approveDisabled}
                    >
                        Approve
                    </Button>
                </Tooltip>
            ))

        const handleApproveAllClick = () => {
            const { data: numberOfHighConfidenceAnswersPerCategory } =
                fetchQuestionAnswersSelectors.numberOfHighConfidenceAnswersPerCategory(
                    { categoryId: category.id },
                    { data: questionSubmissions },
                )

            if (!isOwner) {
                modal.confirm({
                    title: 'Confirm Approve All',
                    icon: <ExclamationCircleOutlined />,
                    content: `Are you sure you want to approve all ${
                        isNumber(numberOfHighConfidenceAnswersPerCategory)
                            ? numberOfHighConfidenceAnswersPerCategory
                            : ''
                    } AI-predicted answers classified with ${getConfidenceLevelNoun(
                        ConfidenceLevels.VERY_CONFIDENT,
                    ).toLowerCase()} in the category ${category.name}?`,
                    okText: 'Approve All',
                    onOk() {
                        approveAll(category.id)
                    },
                    cancelText: 'Cancel',
                })
                return
            }

            const { data: numberOfMyHighConfidenceAnswersPerCategory } =
                fetchQuestionAnswersSelectors.numberOfMyHighConfidenceAnswersPerCategory(
                    { categoryId: category.id, userId },
                    { data: questionSubmissions },
                )

            const isMyAnswer = answer.assigneeId === userId

            if (isMyAnswer) {
                const modalInstance = modal.confirm({
                    title: 'Confirm Approve All',
                    icon: <ExclamationCircleOutlined />,
                    content: `Are you sure you want to approve only your ${
                        isNumber(numberOfMyHighConfidenceAnswersPerCategory)
                            ? numberOfMyHighConfidenceAnswersPerCategory
                            : ''
                    } AI-predicted or all ${
                        isNumber(numberOfHighConfidenceAnswersPerCategory)
                            ? numberOfHighConfidenceAnswersPerCategory
                            : ''
                    } answers classified with ${getConfidenceLevelNoun(
                        ConfidenceLevels.VERY_CONFIDENT,
                    ).toLowerCase()} in the category ${category.name}?`,
                    footer: (_, { CancelBtn }) => (
                        <Flex justify='end' gap={8}>
                            <CancelBtn />
                            <Button
                                type='primary'
                                onClick={() => {
                                    approveAll(category.id)
                                    modalInstance.destroy()
                                }}
                            >
                                Approve Mine Only
                            </Button>
                            <Button
                                type='primary'
                                onClick={() => {
                                    approveAll(category.id, false)
                                    modalInstance.destroy()
                                }}
                            >
                                Approve All
                            </Button>
                        </Flex>
                    ),
                })
            } else {
                modal.confirm({
                    title: 'Confirm Approve All',
                    icon: <ExclamationCircleOutlined />,
                    content: `Are you sure you want to approve all ${
                        isNumber(numberOfHighConfidenceAnswersPerCategory)
                            ? numberOfHighConfidenceAnswersPerCategory
                            : ''
                    } AI-predicted answers assigned to all reviewers${numberOfMyHighConfidenceAnswersPerCategory > 0 ? ' including yours' : ''} classified with ${getConfidenceLevelNoun(
                        ConfidenceLevels.VERY_CONFIDENT,
                    ).toLowerCase()} in the category ${category.name}?`,
                    okText: 'Approve All',
                    onOk() {
                        approveAll(category.id, false)
                    },
                })
            }
        }

        // Show approveAll button as a full-width primary button
        const approveAllButton = showApproveAll && (
            <Button
                type='primary'
                size='large'
                block
                onClick={handleApproveAllClick}
                icon={<FileDoneOutlined />}
                title='Approve all very-confidently-predicted answers for this category'
                loading={approveAllQuestionRubricAnswersByCategoryResult?.isLoading}
                className='mt-8'
                data-test='approve-all-button'
            >
                Approve All
            </Button>
        )

        const categoryProperties = []
        if (answer.categoryId === category.id && answer.confidence) {
            categoryProperties.push(
                <Tag
                    style={{ whiteSpace: 'nowrap' }}
                    key='confidence'
                    color={getConfidenceLevelTagColor(answer.confidence)}
                    bordered={false}
                    className='m-0'
                >
                    {getConfidenceLevelName(answer.confidence)}
                </Tag>,
            )
        }

        if (answerCategoryMismatchPrediction?.currentCategoryId === category.id) {
            categoryProperties.push(
                <Tag color='warning' icon={<WarningOutlined />} className='m-0' key='mismatch' bordered={false}>
                    Historical
                </Tag>,
            )
        }

        if (answerCategoryMismatchPrediction?.predictedCategoryId === category.id) {
            categoryProperties.push(
                <Tag color='success' icon={<CheckCircleOutlined />} className='m-0' key='recommended' bordered={false}>
                    Recommended
                </Tag>,
            )
        }

        let mayBeInadequateTag

        if (
            explanation
                ? explanation?.categoryId === category.id && explanation?.isAdequate === false
                : selectedCategoryId === category.id &&
                  answer.categoryId === category.id &&
                  answer.isAdequate === false &&
                  answer.explainabilityReasoning
        ) {
            mayBeInadequateTag = (
                <Tag
                    color='warning'
                    className='m-0 mt-8'
                    key='explanation'
                    bordered={false}
                    style={{
                        whiteSpace: 'wrap',
                    }}
                >
                    <b>Rubric incompatibility?</b>
                    <br />
                    {explanation?.reasoning || answer.explainabilityReasoning}
                </Tag>
            )
        }

        const style =
            answer.approveStatus > 0 && answer.categoryId === category.id && selectedCategoryId === category.id
                ? {
                      border: `1px solid ${colors.success}`,
                      backgroundColor: 'rgba(0, 255, 0, 0.05)', // Slightly noticeable green background
                      boxShadow: '0 4px 8px rgba(0, 128, 0, 0.3)', // Green shadow
                      transition: 'all 0.3s ease', // Smooth transition
                  }
                : undefined

        return {
            value: category.id,
            label: (
                <Spin
                    spinning={category.id === selectedCategoryId && isFetchingExplanation}
                    tip='Validating...'
                    wrapperClassName='f-height'
                    onMouseEnter={() => handleMouseEnter(category.id)}
                    onMouseLeave={handleMouseLeave}
                >
                    <Flex
                        vertical
                        gap={4}
                        justify='space-between'
                        className='f-height'
                        style={{ lineHeight: '1rem' }}
                        ref={el => (categoryRefs.current[category.id] = el)} // Assign ref for each category
                    >
                        <div>
                            <Text>
                                <PatternIcon
                                    id={category.id}
                                    content={category.grade}
                                    colorId={category.colorId}
                                    suffix='pt'
                                />{' '}
                                {category.name}
                            </Text>
                            {showDetails && (
                                <div
                                    className='category-description'
                                    title={category.description?.length > 270 ? category.description : undefined}
                                >
                                    <MarkdownRenderer
                                        content={truncate(category.description, { length: 270 })}
                                        type='secondary'
                                    />
                                </div>
                            )}
                            {mayBeInadequateTag}
                        </div>
                        <Flex justify='space-between' align='flex-end' gap={4} wrap='wrap'>
                            <Space wrap style={{ flex: 1 }}>
                                {categoryProperties}
                            </Space>
                        </Flex>
                        {/* Full-width approve buttons at the bottom of selected category */}
                        {approveButton}
                        {approveAllButton}
                    </Flex>
                </Spin>
            ),
            style: {
                width: `calc(100% / ${categories.length})`,
                minWidth: 320,
                height: 'auto',
                padding: 8,
                flexShrink: 0,
                ...style,
            },
        }
    })
    const onChange = event => {
        if (readonly) return
        if (event.target.value === selectedCategoryId) onApprove()
        setSelectedCategoryId(event.target.value)
        setCategoryIdToHighlight(event.target.value)
    }

    useShortcutSelection(
        categories,
        onChange,
        onApprove,
        focusedQuestionRubricId !== answer.questionRubricId || readonly,
    )

    return (
        <>
            <Radio.Group
                value={selectedCategoryId}
                options={options}
                onChange={onChange}
                ref={containerRef}
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'nowrap',
                    width: `100%`,
                    overflowX: 'auto',
                    borderRadius: 0,
                }}
                optionType='button'
                className='category-select'
            />
            {reviewQuestionRubricAnswerResult.isError && (
                <Alert
                    className='mt-8'
                    message={reviewQuestionRubricAnswerResult?.error}
                    type='error'
                    showIcon
                    closable
                />
            )}
        </>
    )
}

export default CategorySelect
